import { Box, Tooltip } from "@mui/material";
import { AccessTime, AttachMoney, CheckCircle } from "@mui/icons-material";

export type RagStatus = "red" | "amber" | "green";

export interface RagMarkerData {
  time: RagStatus;
  cost: RagStatus;
  quality: RagStatus;
  areaName: string;
}

interface RagMarkerProps {
  data: RagMarkerData;
  zoomLevel: number;
}

const ZOOM_THRESHOLD = 8;

const getStatusColor = (status: RagStatus): string => {
  switch (status) {
    case "red":
      return "#f44336";
    case "amber":
      return "#ff9800";
    case "green":
      return "#4caf50";
  }
};

const getAggregatedStatus = (
  time: RagStatus,
  cost: RagStatus,
  quality: RagStatus
): RagStatus => {
  const statuses = [time, cost, quality];

  if (statuses.includes("red")) return "red";
  if (statuses.includes("amber")) return "amber";
  return "green";
};

const StatusIcon = ({
  status,
  type,
}: {
  status: RagStatus;
  type: "time" | "cost" | "quality";
}) => {
  const color = getStatusColor(status);
  const IconComponent =
    type === "time" ? AccessTime : type === "cost" ? AttachMoney : CheckCircle;

  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: "50%",
        backgroundColor: color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "2px solid white",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
      }}
    >
      <IconComponent
        sx={{
          fontSize: 14,
          color: "white",
        }}
      />
    </Box>
  );
};

const AggregatedStatusCircle = ({ status }: { status: RagStatus }) => {
  const color = getStatusColor(status);

  return (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: "50%",
        backgroundColor: color,
        border: "2px solid white",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
      }}
    />
  );
};

export const RagMarker = ({ data, zoomLevel }: RagMarkerProps) => {
  const showDetailed = zoomLevel >= ZOOM_THRESHOLD;
  const aggregatedStatus = getAggregatedStatus(
    data.time,
    data.cost,
    data.quality
  );

  const tooltipTitle = (
    <Box sx={{ textAlign: "center" }}>
      <Box sx={{ fontWeight: "bold", mb: 0.5 }}>{data.areaName}</Box>
      <Box sx={{ fontSize: "0.85rem" }}>
        Time: {data.time.toUpperCase()} | Cost: {data.cost.toUpperCase()} |
        Quality: {data.quality.toUpperCase()}
      </Box>
    </Box>
  );

  return (
    <Tooltip title={tooltipTitle} arrow placement="top">
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          padding: showDetailed ? 0.5 : 0,
          backgroundColor: showDetailed ? "background.paper" : "transparent",
          borderRadius: 2,
          boxShadow: showDetailed ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
          cursor: "pointer",
          transition: "transform 0.2s",
          "&:hover": {
            transform: "scale(1.1)",
            boxShadow: showDetailed
              ? "0 4px 12px rgba(0,0,0,0.25)"
              : "0 2px 8px rgba(0,0,0,0.2)",
          },
        }}
      >
        {showDetailed ? (
          <>
            <StatusIcon status={data.time} type="time" />
            <StatusIcon status={data.cost} type="cost" />
            <StatusIcon status={data.quality} type="quality" />
          </>
        ) : (
          <AggregatedStatusCircle status={aggregatedStatus} />
        )}
      </Box>
    </Tooltip>
  );
};
